import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip,
  Collapse,
  List,
  ListItem,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Business as CompanyIcon,
  Science as SampleTypeIcon,
  BloodtypeOutlined as PhlebotomyIcon,
  LocalShipping as ShippingIcon,
  Schedule as ProcessingTimeIcon,
  Description as InstructionsIcon,
  FileDownload as ReportIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  School as ReferenceIcon,
  East as ExternalLinkIcon,
} from '@mui/icons-material';

const TestDetailModal = ({ open, test, onClose, onCompare }) => {
  const [showBiomarkers, setShowBiomarkers] = useState(true);

  if (!test) return null;

  const getSampleTypeColor = (sampleTypeName) => {
    const name = sampleTypeName?.toLowerCase() || '';
    if (name.includes('stool')) return '#FF6B35';
    if (name.includes('serum')) return '#E63946';
    if (name.includes('blood')) return '#D62828';
    if (name.includes('saliva')) return '#00BBF9';
    if (name.includes('urine')) return '#F77F00';
    return '#6C757D';
  };

  // Helper function to check if a field is empty
  const isEmpty = (value) => {
    return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: '#1a1a1a',
          }}
        >
          {test.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InfoIcon />}
            onClick={onCompare}
            sx={{
              color: '#1976d2',
              borderColor: '#1976d2',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Compare Tests
          </Button>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Description Section */}
          {test.description && (
            <Box sx={{ p: 3, borderBottom: '1px solid #e8e8e8' }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#555',
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                }}
              >
                {test.description}
              </Typography>
            </Box>
          )}

          {/* Info Card */}
          {test.testInformationPage && (
            <Box sx={{ p: 3, borderBottom: '1px solid #e8e8e8' }}>
              <Alert
                icon={<ReferenceIcon sx={{ fontSize: 20 }} />}
                sx={{
                  backgroundColor: '#e3f2fd',
                  borderLeft: '4px solid #1976d2',
                  '& .MuiAlert-icon': {
                    color: '#1976d2',
                  },
                }}
              >
                <Link
                  href={test.testInformationPage}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
                    Learn more about this test in the Reference Guide.
                    <ExternalLinkIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }} />
                  </Typography>
                </Link>
                <Typography variant="caption" sx={{ color: '#555' }}>
                  Explore in-depth guides, free Live Classes, and more.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Main Details Grid */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e8e8e8' }}>
            <Grid container spacing={3}>
              {/* Row 1 */}
              <Grid item xs={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#999',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.8,
                    }}
                  >
                    <CompanyIcon sx={{ fontSize: 14 }} />
                    Company
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#1a1a1a',
                      fontSize: '0.95rem',
                    }}
                  >
                    {test.lab?.name || 'Unknown Lab'}
                  </Typography>
                </Box>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#999',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.8,
                    }}
                  >
                    <SampleTypeIcon sx={{ fontSize: 14 }} />
                    Sample Type
                  </Typography>
                  <Chip
                    label={test.sampleType?.name?.toUpperCase() || 'N/A'}
                    size="small"
                    sx={{
                      backgroundColor: getSampleTypeColor(test.sampleType?.name),
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#999',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.8,
                    }}
                  >
                    <PhlebotomyIcon sx={{ fontSize: 14 }} />
                    Phlebotomy
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: test.phlebotomy === 'Required' ? '#d32f2f' : '#388e3c',
                      fontSize: '0.95rem',
                    }}
                  >
                    {test.phlebotomy || 'Not Required'}
                  </Typography>
                </Box>
              </Grid>

              {/* Row 3 */}
              {!isEmpty(test.shippingTimeMin) && !isEmpty(test.shippingTimeMax) && (
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#999',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.8,
                      }}
                    >
                      <ShippingIcon sx={{ fontSize: 14 }} />
                      Shipping Time
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: '#1a1a1a',
                        fontSize: '0.95rem',
                      }}
                    >
                      {`${test.shippingTimeMin}-${test.shippingTimeMax} Business Days`}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {!isEmpty(test.processingTime) && (
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#999',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.8,
                      }}
                    >
                      <ProcessingTimeIcon sx={{ fontSize: 14 }} />
                      Avg. Sample Processing Time
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: '#1a1a1a',
                        fontSize: '0.95rem',
                      }}
                    >
                      {`${test.processingTime} Business Days`}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Row 4 */}
              {!isEmpty(test.collectionInstructions) && (
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#999',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.8,
                      }}
                    >
                      <InstructionsIcon sx={{ fontSize: 14 }} />
                      Patient Collection Instructions
                    </Typography>
                    <Link
                      href={test.collectionInstructions}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#1976d2',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      View
                      <ExternalLinkIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                </Grid>
              )}

              {!isEmpty(test.sampleReport) && (
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#999',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.8,
                      }}
                    >
                      <ReportIcon sx={{ fontSize: 14 }} />
                      Sample Report
                    </Typography>
                    <Link
                      href={test.sampleReport}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#1976d2',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      View
                      <ExternalLinkIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                </Grid>
              )}

              {/* Row 5 - Location */}
              {test.location && (
                <>
                  <Grid item xs={6}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: '#999',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 0.8,
                        }}
                      >
                        <LocationIcon sx={{ fontSize: 14 }} />
                        Patient Based-Location Restrictions
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#1a1a1a',
                          fontSize: '0.95rem',
                        }}
                      >
                        {test.location?.name || 'No Restrictions'}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}

              {/* Lab Reference */}
              {!isEmpty(test.labReference) && (
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#999',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.8,
                      }}
                    >
                      Lab Reference
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: '#1a1a1a',
                        fontSize: '0.95rem',
                        wordBreak: 'break-word',
                      }}
                    >
                      {test.labReference}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Biomarkers Section */}
          {test.biomarkers && test.biomarkers.length > 0 && (
            <Box sx={{ borderBottom: '1px solid #e8e8e8' }}>
              <Box
                onClick={() => setShowBiomarkers(!showBiomarkers)}
                sx={{
                  p: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#fafafa',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: '#1a1a1a',
                    fontSize: '1rem',
                  }}
                >
                  Biomarkers
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={test.biomarkers.length}
                    size="small"
                    sx={{
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 700,
                    }}
                  />
                  <IconButton size="small" onClick={() => setShowBiomarkers(!showBiomarkers)}>
                    {showBiomarkers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={showBiomarkers}>
                <Box sx={{ px: 3, pb: 3 }}>
                  <List sx={{ p: 0 }}>
                    {test.biomarkers.map((biomarker, index) => (
                      <Box key={index}>
                        <ListItem
                          sx={{
                            px: 0,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#555',
                              fontSize: '0.95rem',
                            }}
                          >
                            {biomarker}
                          </Typography>
                        </ListItem>
                        {index < test.biomarkers.length - 1 && (
                          <Divider sx={{ my: 0.5 }} />
                        )}
                      </Box>
                    ))}
                  </List>
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TestDetailModal;

